import React, { useEffect, useState } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useERDStore } from './erdStore';
import ERDTopBar from './ERDTopBar';
import ERDSidebar from './ERDSidebar';
import ERDRightPanel from './ERDRightPanel';
import FlowCanvas from './FlowCanvas';
import { Plus, FileText, Upload, Trash2, RotateCcw, Clock } from 'lucide-react';
import './ERDPage.css';

function DocumentList() {
  const { documents, documentsLoading, loadDocuments, createDocument, openDocument, deleteDocument } = useERDStore();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  async function handleCreate() {
    if (!newName.trim()) return;
    try {
      await createDocument(newName.trim());
    } catch (e) { /* handled in store */ }
    setCreating(false);
    setNewName('');
  }

  async function handleDelete(id) {
    try {
      await deleteDocument(id);
      setConfirmDelete(null);
    } catch (e) { /* handled in store */ }
  }

  function handleImport() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.ferd.json';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const data = JSON.parse(text);
        if (data.pages) {
          const { importFromFile } = useERDStore.getState();
          await importFromFile(data);
        }
      } catch (err) {
        console.error('Failed to import:', err);
      }
    };
    input.click();
  }

  return (
    <div className="erd-doc-list">
      <div className="erd-doc-list-header">
        <div>
          <h2>ERD Documents</h2>
          <p className="erd-doc-list-subtitle">Firestore schema diagrams</p>
        </div>
        <div className="erd-doc-list-actions">
          <button onClick={handleImport} className="erd-doc-action-btn">
            <Upload size={14} /> Import
          </button>
          <button onClick={() => { setCreating(true); setNewName('New ERD'); }} className="erd-doc-action-btn erd-doc-action-primary">
            <Plus size={14} /> New Document
          </button>
        </div>
      </div>

      {creating && (
        <div className="erd-doc-create-form">
          <input
            autoFocus
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setCreating(false); }}
            className="erd-doc-create-input"
            placeholder="Document name"
          />
          <button onClick={handleCreate} className="erd-doc-action-btn erd-doc-action-primary">Create</button>
          <button onClick={() => setCreating(false)} className="erd-doc-action-btn">Cancel</button>
        </div>
      )}

      {documentsLoading ? (
        <div className="erd-doc-empty">Loading documents...</div>
      ) : documents.length === 0 ? (
        <div className="erd-doc-empty">
          <FileText size={40} />
          <h3>No ERD documents yet</h3>
          <p>Create a new document or import an existing .ferd.json file to get started.</p>
        </div>
      ) : (
        <div className="erd-doc-grid">
          {documents.map(doc => (
            <div key={doc.id} className="erd-doc-card" onClick={() => openDocument(doc.id)}>
              <div className="erd-doc-card-icon"><FileText size={24} /></div>
              <div className="erd-doc-card-info">
                <h3 className="erd-doc-card-name">{doc.name}</h3>
                <p className="erd-doc-card-meta">
                  {doc.pageCount || 0} page{(doc.pageCount || 0) !== 1 ? 's' : ''}
                  {doc.updatedAt && <> &middot; {new Date(doc.updatedAt).toLocaleDateString()}</>}
                </p>
              </div>
              <div className="erd-doc-card-actions" onClick={e => e.stopPropagation()}>
                {confirmDelete === doc.id ? (
                  <div className="erd-doc-confirm-delete">
                    <span>Delete?</span>
                    <button onClick={() => handleDelete(doc.id)} className="erd-doc-confirm-yes">Yes</button>
                    <button onClick={() => setConfirmDelete(null)} className="erd-doc-confirm-no">No</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmDelete(doc.id)} className="erd-doc-card-delete" title="Delete">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RevisionPanel({ onClose }) {
  const { revisions, revisionsLoading, loadRevisions, restoreRevision } = useERDStore();
  const [confirmRestore, setConfirmRestore] = useState(null);

  useEffect(() => { loadRevisions(); }, [loadRevisions]);

  function handleRestore(rev) {
    restoreRevision(rev);
    setConfirmRestore(null);
    onClose();
  }

  return (
    <div className="erd-revision-panel">
      <div className="erd-revision-header">
        <h3><Clock size={14} /> Revision History</h3>
        <button onClick={onClose} className="erd-revision-close">&times;</button>
      </div>
      <div className="erd-revision-body">
        {revisionsLoading ? (
          <p className="erd-revision-empty">Loading...</p>
        ) : revisions.length === 0 ? (
          <p className="erd-revision-empty">No revisions yet. Revisions are created each time you save.</p>
        ) : (
          revisions.map(rev => (
            <div key={rev.id} className="erd-revision-item">
              <div className="erd-revision-info">
                <span className="erd-revision-date">{new Date(rev.createdAt).toLocaleString()}</span>
                <span className="erd-revision-pages">{rev.pageCount} page{rev.pageCount !== 1 ? 's' : ''}</span>
              </div>
              {confirmRestore === rev.id ? (
                <div className="erd-doc-confirm-delete">
                  <span>Restore?</span>
                  <button onClick={() => handleRestore(rev)} className="erd-doc-confirm-yes">Yes</button>
                  <button onClick={() => setConfirmRestore(null)} className="erd-doc-confirm-no">No</button>
                </div>
              ) : (
                <button onClick={() => setConfirmRestore(rev.id)} className="erd-revision-restore" title="Restore this revision">
                  <RotateCcw size={12} /> Restore
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function DocumentEditor() {
  const [showRevisions, setShowRevisions] = useState(false);

  return (
    <div className="erd-editor">
      <ERDTopBar onShowRevisions={() => setShowRevisions(v => !v)} />
      <div className="erd-editor-body">
        <ERDSidebar />
        <div className="erd-canvas-container">
          <FlowCanvas />
        </div>
        <ERDRightPanel />
        {showRevisions && <RevisionPanel onClose={() => setShowRevisions(false)} />}
      </div>
    </div>
  );
}

export default function ERDPage() {
  const currentDocId = useERDStore(s => s.currentDocId);

  return (
    <ReactFlowProvider>
      <div className="erd-page">
        {currentDocId ? <DocumentEditor /> : <DocumentList />}
      </div>
    </ReactFlowProvider>
  );
}
